import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { NAVIGATE_PATH as PATH } from '../../../helpers/constants';
import { getOpsContacts } from '../../../requests';
import {
  btnPrimary,
  Card,
  EmptyState,
  inputClass,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  rowClickClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Toolbar,
  SortTh,
  useListSort,
} from '../ui';

const OpsContacts: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const accountId = searchParams.get('accountId') ?? '';
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [scope, setScope] = useState<'any' | 'name' | 'email' | 'account'>(
    'any',
  );
  const [page, setPage] = useState(1);
  const { sortKey, sortDir, toggleSort } = useListSort();

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['opsContactsList', q, scope, accountId, sortKey, sortDir, page],
    queryFn: () =>
      getOpsContacts({
        sort: sortKey || undefined,
        dir: sortKey ? sortDir : undefined,
        q: q || undefined,
        scope: scope === 'any' ? undefined : scope,
        accountId: accountId || undefined,
        page,
      }),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Contacts"
        meta={
          data ? `${data.total.toLocaleString()} people on record` : undefined
        }
        actions={
          <button
            className={btnPrimary}
            onClick={() => navigate(PATH.opsContactNew)}
          >
            New contact
          </button>
        }
      />
      <Toolbar>
        <input
          className={`${inputClass} w-72`}
          placeholder={
            scope === 'account'
              ? 'Search by account name…'
              : scope === 'email'
              ? 'Search email…'
              : scope === 'name'
              ? 'Search name…'
              : 'Search name or email…'
          }
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={inputClass}
          value={scope}
          onChange={(e) => {
            setScope(e.target.value as typeof scope);
            setPage(1);
          }}
          title="What the search box matches"
        >
          <option value="any">Name or email</option>
          <option value="name">Name only</option>
          <option value="email">Email only</option>
          <option value="account">Account name</option>
        </select>
        {accountId && (
          <button
            className="rounded-full border border-blue-200 bg-blue-100/60 px-2.5 py-1 text-xs text-blue-primary"
            onClick={() => setSearchParams({})}
            title="Show every contact"
          >
            {data?.items[0]?.accountName ?? 'one account'} only ×
          </button>
        )}
      </Toolbar>
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <Card>
            <div className="overflow-x-auto">
              <table className={`${tableClass} min-w-[900px]`}>
                <thead className={theadClass}>
                  <tr>
                    <SortTh
                      k="name"
                      label="Name"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="email"
                      label="Email"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="title"
                      label="Title"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="account"
                      label="Account"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="roles"
                      label="Roles"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="phone"
                      label="Phone"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((c) => (
                    <tr
                      key={c.id}
                      className={rowClickClass}
                      onClick={() =>
                        navigate(PATH.opsContactEdit.replace(':id', c.id))
                      }
                    >
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {c.name ?? '—'}
                        {c.matchedOn === 'email' && (
                          <span className="ml-2">
                            <Pill value="matched on email" tone="info" />
                          </span>
                        )}
                        {c.left && (
                          <span className="ml-2">
                            <Pill value="left company" tone="neutral" />
                          </span>
                        )}
                        {c.localOrigin && (
                          <span className="ml-2">
                            <Pill value="created here" tone="good" />
                          </span>
                        )}
                      </td>
                      <td className={tdClass}>{c.email ?? '—'}</td>
                      <td className={tdClass}>{c.title ?? '—'}</td>
                      <td className={tdClass}>{c.accountName ?? '—'}</td>
                      <td className={tdClass}>{c.roles ?? '—'}</td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {c.phone ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data && data.items.length === 0 && (
              <EmptyState
                title="No contacts match"
                hint="Try a different name or email."
              />
            )}
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
        </PageBody>
      )}
    </>
  );
};

export default OpsContacts;
